import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  FormControl,
  FormLabel,
  HStack,
  Input,
  Switch,
  Text,
} from '@chakra-ui/react';
import { ColorOption, colorOptionValue } from './Dropdown';

interface NoteDrawerProps {
  forceHideNoteTaking: boolean;
  selectedNoteId: string;
  notes: any[];
  isOpen: boolean;
  isUpdateCommentLoading: boolean;
  showNoteTaking: boolean;
  toggleNoteTaking: () => void;
  onClose: () => void;
  updateNoteComment: (noteId: string, comment: string) => void;
  updateNoteData: (noteId: string, data: any) => void;
}

const NoteDrawer = (props: NoteDrawerProps) => {
  const {
    forceHideNoteTaking,
    selectedNoteId,
    isUpdateCommentLoading,
    notes,
    isOpen,
    showNoteTaking,
    toggleNoteTaking,
    onClose,
    updateNoteComment,
    updateNoteData,
  } = props;
  const [newCommentValues, setNewCommentValues] = useState<
    Record<string, string>
  >({});
  const btnRef = useRef<any>();
  const [focusedNote, setFocusedNote] = useState(0);
  const interval = useRef<any>(null);

  const sortedNotes = [...notes]
    .sort((a, b) => (b.id === selectedNoteId ? 1 : -1))
    .filter((note) => note.color !== ColorOption.transparent);

  useEffect(() => {
    interval.current = setInterval(() => {
      sortedNotes.forEach((note) => {
        const el = document.querySelector(`#comment-mark-${note.id}`);
        if (el) {
          el.innerHTML = note.comment;
        }
      });
    }, 1000);
    return () => {
      if (interval.current) {
        clearInterval(interval.current);
      }
    };
  }, [sortedNotes]);

  return (
    <Drawer
      isOpen={isOpen}
      placement="right"
      onClose={onClose}
      finalFocusRef={btnRef}
      size="md"
    >
      <DrawerContent>
        <DrawerCloseButton />
        <DrawerHeader>Notes</DrawerHeader>

        <DrawerBody boxShadow="inset 0px 11px 8px -10px #aaa, inset 0px -11px 8px -10px #aaa">
          {forceHideNoteTaking ? (
            <Text>Coming soon!</Text>
          ) : (
            <>
              <FormControl display="flex" alignItems="center" mb={8} mt={8}>
                <FormLabel mb="0">Show notes in text?</FormLabel>
                <Switch
                  margin={0}
                  size="lg"
                  isChecked={showNoteTaking}
                  onChange={toggleNoteTaking}
                />
              </FormControl>
              {sortedNotes.map((note, index) => {
                return (
                  <Box
                    key={note.id}
                    boxShadow="#00000020 0px 2px 8px"
                    borderRadius={4}
                    p={4}
                    mb={2}
                    _hover={{ cursor: 'pointer', background: '#EAEAEA' }}
                    onClick={() => setFocusedNote(index)}
                  >
                    <Text
                      textOverflow="ellipsis"
                      overflow="hidden"
                      display="inline-block"
                      mb={2}
                      background={colorOptionValue[note.color as ColorOption]}
                    >
                      {note.text}
                    </Text>
                    {index === focusedNote || note.comment ? (
                      <>
                        <Input
                          mb={2}
                          autoFocus={index === focusedNote}
                          noOfLines={3}
                          defaultValue={note.comment || ''}
                          placeholder="Comment here..."
                          onChange={(e) => {
                            newCommentValues[note.id] = e.target.value;
                            setNewCommentValues({ ...newCommentValues });
                          }}
                        />
                        {newCommentValues[note.id] &&
                          newCommentValues[note.id] !== note.comment && (
                            <Button
                              isLoading={isUpdateCommentLoading}
                              mb={2}
                              colorScheme="blue"
                              onClick={() => {
                                updateNoteComment(
                                  note.id,
                                  newCommentValues[note.id]!,
                                );
                              }}
                            >
                              Save
                            </Button>
                          )}
                      </>
                    ) : null}
                    <HStack
                      alignItems="center"
                      justifyContent="space-between"
                      mt={2}
                    >
                      <Text mb={0}>
                        {new Date(note.updatedAt).toLocaleString()}
                      </Text>
                      <Button
                        size="xs"
                        colorScheme="red"
                        onClick={() => {
                          updateNoteData(note.id, {
                            ...note,
                            color: ColorOption.transparent,
                          });
                        }}
                      >
                        Delete
                      </Button>
                    </HStack>
                  </Box>
                );
              })}{' '}
            </>
          )}
        </DrawerBody>

        <DrawerFooter>
          <Button variant="outline" mr={3} onClick={onClose}>
            Close
          </Button>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
};

export default NoteDrawer;
